import { useMemo } from "react";
import { AgentsIcon, Box, Row } from "@hightouch/htui";
import Prando from "prando";

/**
 * htui's Avatar renders initials or a gradient and takes no icon, so an agent
 * can't use it. Its palette, seeding, and sizes are mirrored here instead, so
 * an agent's avatar sits beside a person's. Drop this once Avatar takes an icon.
 */
const AVATAR_COLORS = ["teal", "indigo", "orange", "blue", "green", "pink"];

/**
 * Avatar's own sizes, keyed the same way, each paired with an icon that fills
 * about 60% of the circle.
 *
 * The icon is sized through `fontSize` rather than `boxSize`: `AgentsIcon`
 * renders at `width: 1em; height: 1em`, so it scales off the inherited font
 * size. A box set on it does nothing, which is why it used to stay small
 * inside the larger circles.
 *
 * So the icon takes a `fontSizes` token rather than a length: 12px, 14px and
 * 20px against circles of 20px, 24px and 32px.
 */
const AVATAR_SIZES = {
  sm: { box: 5, icon: "sm" },
  md: { box: 6, icon: "md" },
  lg: { box: 8, icon: "2xl" },
} as const;

type Props = {
  name: string;
  size: keyof typeof AVATAR_SIZES;
};

/**
 * A review agent's avatar. Circular and colour-seeded like a person's, but
 * always an icon: initials would imply it were a person.
 */
export const ReviewAgentAvatar = ({ name, size }: Props) => {
  const { box, icon } = AVATAR_SIZES[size];
  const color = useMemo(
    () => AVATAR_COLORS[new Prando(name).nextInt(0, AVATAR_COLORS.length - 1)],
    [name],
  );

  return (
    <Row
      align="center"
      justify="center"
      boxSize={box}
      flexShrink={0}
      borderRadius="full"
      borderWidth="hairline"
      borderStyle="solid"
      bg={`${color}.100`}
      borderColor={`${color}.300`}
    >
      <Box as={AgentsIcon} fontSize={icon} color={`${color}.700`} />
    </Row>
  );
};
